import { forwardRef, type ReactNode } from 'react';
import { IconButton, type IconButtonProps } from './IconButton.js';
import { cn } from '../lib/utils.js';

/* ===========================================================================
 * IconTool — the 38px square tool button that sits at the trailing end of the
 * one toolbar. Refresh, and column layout.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A WRAPPER AROUND `IconButton` AND NOT A NEW PRIMITIVE.
 *
 * The brief was to check whether `IconButton` could express this as a variant
 * and extend it if so, rather than adding a near-duplicate. It can express
 * almost all of it and it cannot express two things, so the answer is neither
 * "a variant" nor "a new primitive" — it is a THIN COMPOSITION:
 *
 *   what IconButton already gets right, and this therefore inherits rather than
 *   re-states — the `.state-layer` hover/focus/press ramp, the shared focus
 *   ring, the coarse-pointer 44px floor, `type="button"` defaulted, and above
 *   all a REQUIRED `label` that becomes both the accessible name and the
 *   tooltip. An icon-only control with no name is invisible to a screen reader,
 *   and re-implementing this shape from scratch is how that bug comes back.
 *
 *   what it cannot express — (1) the size. `IconButton`'s three sizes are
 *   32/40/44px and the tool is 38px; adding a fourth to a five-variant `cva`
 *   for one call site is worse than overriding it here. (2) The rest ink.
 *   `variant="outline"` is `text-foreground`; the toolbar's tools sit at
 *   `--foreground-tertiary` and come up to full ink on hover, which is what
 *   keeps two grey glyphs from competing with the search field beside them.
 *
 * So: `IconButton` with `variant="outline"` plus this file's geometry and ink.
 * One control family, one focus treatment, one label rule — and if the ramp or
 * the ring is ever retuned, this moves with it instead of being the copy nobody
 * remembers to update.
 *
 * GEOMETRY is `min-h`/`min-w` and not `h`/`w`. `IconButton`'s size variants use
 * fixed `h-10 w-10`, so the override has to be a definite `h-auto w-auto`
 * FOLLOWED by the floors — tailwind-merge keeps `h-*` and `min-h-*` in separate
 * groups, so a bare floor would land alongside the fixed height rather than
 * replacing it and the control would render 40px with a 38px minimum. This is
 * also the anatomy rule the handoff's README states: padding plus a minimum,
 * never a fixed height, so a tool never clips its own glyph.
 *
 * ---------------------------------------------------------------------------
 * `active` — for a tool that OPENS something and stays lit while it is open (the
 * column-layout panel). It sets `aria-pressed`, not just a fill: a tool whose
 * state is visual-only is unreadable to anyone who never sees the tint, and
 * §1 of docs/design-rules.md does not make an exception for controls. Same
 * reasoning as `FilterChip`'s `selected`.
 *
 * A DISABLED TOOL DOES NOT ANIMATE — rules §1, motion promises something will
 * happen. `IconButton` already drops pointer events when disabled, which stops
 * the state layer; `disabled:transition-none` is the rest of it.
 *
 * RTL: nothing here is directional. A square is a square.
 * ======================================================================== */

export interface IconToolProps
  extends Omit<IconButtonProps, 'variant' | 'size' | 'adornment' | 'aria-pressed'> {
  /** The glyph. 16px is the toolbar's size — `<RefreshCw size={16} />`. */
  children: ReactNode;
  /**
   * The tool is currently on — its panel is open, its mode is engaged. Carried
   * as `aria-pressed` as well as a tint, so the state is never colour-only.
   * Leave it undefined for a tool that just does something once (refresh).
   */
  active?: boolean;
}

export const IconTool = forwardRef<HTMLButtonElement, IconToolProps>(
  ({ className, active, ...props }, ref) => (
    <IconButton
      ref={ref}
      variant="outline"
      aria-pressed={active}
      className={cn(
        // See the header note: `h-auto w-auto` first to unseat the variant's
        // fixed square, then the 38px floors.
        'h-auto w-auto min-h-control min-w-control p-0',
        'disabled:transition-none',
        active
          ? // Lit. The brand tint plus brand ink is the `tint`/`deep` pairing
            // the token layer documents — never the solid `--primary` under a
            // 16px glyph, which reads as a primary action rather than a
            // toggled tool.
            'border-primary bg-primary-tint text-primary-deep'
          : // At rest the tool is quieter than the labelled controls beside it
            // and comes up to full ink under the pointer. `border-strong` on
            // hover is the handoff's move: the edge sharpens rather than the
            // fill shouting.
            'bg-surface text-foreground-tertiary hover:border-border-strong hover:bg-muted hover:text-foreground',
        className
      )}
      {...props}
    />
  )
);
IconTool.displayName = 'IconTool';
